import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Stack,
    Typography,
} from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import useChartAiBridgeStore from '../../../../stores/useChartAiBridgeStore';
import {isDisplayableEntityLabel} from '../../services/inlineComponents/decodeInlineComponentStream';
import {
    materializeChartMutations,
    relatedArrowMutations,
    buildResolvedAddBatch,
} from '../../../whiteboards/ai/materializeChartMutations';
import {
    formatChartErrorForUser,
    formatChartWarningForUser,
    isAnalystVisibleChartPlanSummary,
    isHiddenChartMutationMessage,
    isInternalChartWarning,
} from '../../../whiteboards/ai/chartUserMessages';
import {resolvePreviewEntityImage} from '../../utils/mutationPreviewEntityImage';

export {isInternalChartWarning} from '../../../whiteboards/ai/chartUserMessages';

/**
 * Build entityRef → human label map from plan mutations.
 * @param {Array<object>} mutations
 * @returns {Map<string, string>}
 */
export function buildMutationLabelIndex(mutations) {
    const index = new Map();
    for (const mutation of mutations || []) {
        if (mutation?.op === 'add_entity_card' && mutation.entityRef) {
            if (isDisplayableEntityLabel(mutation.label, mutation.entityRef)) {
                index.set(mutation.entityRef, mutation.label);
            }
        }
        if (mutation?.op === 'update_entity_card' && mutation.entityRef && mutation.label) {
            index.set(mutation.entityRef, mutation.label);
        }
        if (mutation?.op === 'add_arrow') {
            if (mutation.fromEntityRef && mutation.fromLabel) {
                index.set(mutation.fromEntityRef, mutation.fromLabel);
            }
            if (mutation.toEntityRef && mutation.toLabel) {
                index.set(mutation.toEntityRef, mutation.toLabel);
            }
        }
    }
    return index;
}

function displayName(label, ref, fallback = 'entity') {
    if (isDisplayableEntityLabel(label, ref)) {
        return String(label).trim();
    }
    return fallback;
}

function displayRef(ref, labelIndex) {
    if (!ref) return 'entity';
    return displayName(labelIndex.get(ref), ref, 'entity');
}

function formatConnectionLabel(from, to, relation) {
    if (relation) {
        return `${from} → ${to} (${relation})`;
    }
    return `${from} → ${to}`;
}

export function describeMutation(mutation, labelIndex = new Map()) {
    if (!mutation || typeof mutation !== 'object') {
        return 'Unknown change';
    }
    if (mutation.op === 'create_local_entity') {
        return displayName(mutation.label, mutation.entityRef);
    }
    if (mutation.op === 'pick_entity') {
        return `Choose ${displayName(mutation.label, mutation.entityRef)}`;
    }
    if (mutation.op === 'add_entity_card') {
        return displayName(mutation.label, mutation.entityRef);
    }
    if (mutation.op === 'update_entity_card') {
        return displayName(mutation.label, mutation.entityRef);
    }
    if (mutation.op === 'add_arrow') {
        const from = mutation.fromLabel || displayRef(mutation.fromEntityRef, labelIndex);
        const to = mutation.toLabel || displayRef(mutation.toEntityRef, labelIndex);
        const relation = mutation.label || mutation.relation;
        return formatConnectionLabel(from, to, relation);
    }
    if (mutation.op === 'update_arrow') {
        const from = mutation.fromLabel || displayRef(mutation.fromEntityRef, labelIndex);
        const to = mutation.toLabel || displayRef(mutation.toEntityRef, labelIndex);
        const relation = mutation.label || mutation.relation;
        return formatConnectionLabel(from, to, relation);
    }
    return mutation.op || 'Unknown change';
}

function mutationActionVerb(mutation) {
    if (mutation?.op === 'add_arrow') {
        return 'Connect';
    }
    if (mutation?.op === 'update_arrow' || mutation?.op === 'update_entity_card') {
        return 'Update';
    }
    return 'Add';
}

export function isDisplayableMutation(mutation, labelIndex = new Map()) {
    if (!mutation || typeof mutation !== 'object') {
        return false;
    }
    if (mutation.op === 'add_entity_card') {
        return isDisplayableEntityLabel(mutation.label || mutation.entityRef, mutation.entityRef);
    }
    if (mutation.op === 'create_local_entity') {
        return isDisplayableEntityLabel(mutation.label, null);
    }
    if (mutation.op === 'pick_entity') {
        return isDisplayableEntityLabel(mutation.label, null)
            && Array.isArray(mutation.candidates)
            && mutation.candidates.length > 0;
    }
    if (mutation.op === 'add_arrow') {
        const fromLabel = mutation.fromLabel || labelIndex.get(mutation.fromEntityRef) || mutation.fromEntityRef;
        const toLabel = mutation.toLabel || labelIndex.get(mutation.toEntityRef) || mutation.toEntityRef;
        return isDisplayableEntityLabel(fromLabel, mutation.fromEntityRef)
            && isDisplayableEntityLabel(toLabel, mutation.toEntityRef);
    }
    return true;
}

export function filterDisplayableMutations(mutations) {
    const labelIndex = buildMutationLabelIndex(mutations);
    return (mutations || []).filter((mutation) => isDisplayableMutation(mutation, labelIndex));
}

const HIDDEN_ID_RE = /^(Q\d+|D[1-9]\d*|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
const EMBEDDED_ID_RE = /\s*\((?:Q\d+|D[1-9]\d*)\)|\b(?:Q\d+|D[1-9]\d*)\b/gi;

export function isBulkApplyReadyMutation(mutation) {
    if (!mutation || typeof mutation !== 'object') {
        return false;
    }
    return mutation.op === 'add_entity_card'
        || mutation.op === 'add_arrow'
        || mutation.op === 'create_local_entity';
}

function sanitizeCandidateText(value) {
    if (value == null) {
        return '';
    }
    const cleaned = String(value).replace(EMBEDDED_ID_RE, ' ').replace(/\s+/g, ' ').trim();
    if (!cleaned || HIDDEN_ID_RE.test(cleaned)) {
        return '';
    }
    return cleaned;
}

function normalizeCandidateLabel(label) {
    return String(label || '').trim().toLowerCase();
}

export function candidateDistinguishingParts(candidate) {
    if (!candidate || typeof candidate !== 'object') {
        return [];
    }
    const aliases = Array.isArray(candidate.aliases)
        ? candidate.aliases.map(sanitizeCandidateText).filter(Boolean)
        : [];
    const alsoKnownAs = sanitizeCandidateText(candidate.alsoKnownAs)
        || (aliases.length ? `also known as ${aliases[0]}` : '');
    const country = sanitizeCandidateText(candidate.country);
    const region = sanitizeCandidateText(candidate.region);
    const place = [region, country].filter(Boolean).join(', ');
    return [
        sanitizeCandidateText(candidate.description),
        place,
        sanitizeCandidateText(candidate.entityType),
        alsoKnownAs ? (alsoKnownAs.startsWith('also known as') ? alsoKnownAs : `also known as ${alsoKnownAs}`) : '',
    ].filter(Boolean);
}

export function filterDistinctPickCandidates(candidates) {
    const list = Array.isArray(candidates) ? candidates : [];
    const describedLabels = new Set(
        list
            .filter((candidate) => candidateDistinguishingParts(candidate).length > 0)
            .map((candidate) => normalizeCandidateLabel(candidate?.label))
    );
    const keptEmpty = new Set();
    const out = [];
    for (const candidate of list) {
        if (!candidate || typeof candidate !== 'object') {
            continue;
        }
        const labelKey = normalizeCandidateLabel(candidate.label);
        const parts = candidateDistinguishingParts(candidate);
        if (parts.length === 0) {
            if (describedLabels.has(labelKey)) {
                continue;
            }
            if (keptEmpty.has(labelKey)) {
                continue;
            }
            keptEmpty.add(labelKey);
        }
        out.push(candidate);
    }
    return out;
}

export function uniqueCandidateSubtitle(candidate, siblings = []) {
    const parts = candidateDistinguishingParts(candidate);
    if (parts.length === 0) {
        return '';
    }
    const others = (siblings || []).filter((other) => other !== candidate);
    for (let count = 1; count <= parts.length; count += 1) {
        const subtitle = parts.slice(0, count).join(' · ');
        const conflict = others.some((other) => (
            candidateDistinguishingParts(other).slice(0, count).join(' · ') === subtitle
        ));
        if (!conflict) {
            return subtitle;
        }
    }
    return parts.join(' · ');
}

export function mutationKey(mutation, index = 0) {
    return [
        mutation?.op || 'unknown',
        mutation?.entityRef || '',
        mutation?.fromEntityRef || '',
        mutation?.toEntityRef || '',
        index,
    ].join(':');
}

/**
 * Build human-readable work-log lines after chart mutations are applied.
 *
 * @param {Array<object>} appliedMutations original mutations that were approved
 * @returns {string[]}
 */
export function buildChartApplyWorkLogEntries(appliedMutations) {
    const entries = [];
    for (const mutation of appliedMutations || []) {
        if (mutation?.op === 'create_local_entity') {
            const label = mutation.label || 'entity';
            entries.push(`Created "${label}" and added to chart`);
            continue;
        }
        if (mutation?.op === 'pick_entity') {
            continue;
        }
        if (mutation?.op === 'add_entity_card') {
            const label = displayName(mutation.label, mutation.entityRef, '');
            if (label) {
                const created = /^D[1-9]\d*$/i.test(String(mutation.entityRef || ''));
                entries.push(created
                    ? `Created "${label}" and added to chart`
                    : `Added "${label}" to chart`);
            }
            continue;
        }
        if (mutation?.op === 'add_arrow') {
            const relation = mutation.label || mutation.relation;
            const from = displayName(mutation.fromLabel, mutation.fromEntityRef, '');
            const to = displayName(mutation.toLabel, mutation.toEntityRef, '');
            if (from && to) {
                entries.push(relation
                    ? `Connected ${from} → ${to} (${relation})`
                    : `Connected ${from} → ${to}`);
            }
        }
    }
    return entries;
}

/**
 * Suppress "X already on chart" when X is only a connect-to-existing target
 * (plan correctly skips re-adding and still connects).
 */
export function filterPreviewWarnings(plan) {
    const warnings = Array.isArray(plan?.warnings) ? plan.warnings : [];
    if (!warnings.length) return warnings;

    const connectLabels = new Set();
    const connectRefs = new Set();
    for (const mutation of plan?.mutations || []) {
        if (mutation?.op !== 'add_arrow') continue;
        if (mutation.fromEntityRef) connectRefs.add(String(mutation.fromEntityRef).toLowerCase());
        if (mutation.toEntityRef) connectRefs.add(String(mutation.toEntityRef).toLowerCase());
        if (mutation.fromLabel) connectLabels.add(String(mutation.fromLabel).toLowerCase());
        if (mutation.toLabel) connectLabels.add(String(mutation.toLabel).toLowerCase());
    }

    const seen = new Set();
    const formatted = [];
    for (const warning of warnings) {
        if (typeof warning !== 'string') continue;
        if (isInternalChartWarning(warning)) continue;
        const alreadyOnChartName = extractAlreadyOnChartName(warning);
        if (alreadyOnChartName) {
            const key = alreadyOnChartName.toLowerCase();
            if (connectLabels.has(key) || connectRefs.has(key)) {
                continue;
            }
        }
        const friendly = formatChartWarningForUser(warning);
        if (!friendly || seen.has(friendly)) continue;
        seen.add(friendly);
        formatted.push(friendly);
    }
    return formatted;
}

const COMPACT_MUTATION_ACTION_SX = {
    minWidth: 0,
    px: 0.75,
    py: 0.125,
    fontSize: 11,
    lineHeight: 1.2,
    height: 22,
};

function findChartEntityImage(ref, chartEntities = [], mutation = null) {
    return resolvePreviewEntityImage(ref, chartEntities, mutation);
}

export function resolveMutationImageUrl(mutation, chartEntities = []) {
    if (!mutation || typeof mutation !== 'object') {
        return '';
    }
    if (mutation.imageUrl) {
        return mutation.imageUrl;
    }
    if (mutation.image) {
        return mutation.image;
    }
    const refs = [mutation.entityRef, mutation.fromEntityRef, mutation.toEntityRef].filter(Boolean);
    for (const ref of refs) {
        const imageUrl = findChartEntityImage(ref, chartEntities, mutation);
        if (imageUrl) {
            return imageUrl;
        }
    }
    return '';
}

export function resolveMutationEndpointImages(mutation, chartEntities = []) {
    if (!mutation || typeof mutation !== 'object') {
        return {fromUrl: '', toUrl: ''};
    }
    if (mutation.op === 'add_arrow' || mutation.op === 'update_arrow') {
        return {
            fromUrl: findChartEntityImage(mutation.fromEntityRef, chartEntities, mutation),
            toUrl: findChartEntityImage(mutation.toEntityRef, chartEntities, mutation),
        };
    }
    return {
        fromUrl: resolveMutationImageUrl(mutation, chartEntities),
        toUrl: '',
    };
}

function avatarInitials(label) {
    const text = String(label || '').trim();
    if (!text) {
        return '?';
    }
    return text.slice(0, 1).toUpperCase();
}

function MutationEntityAvatar({src, label, testId, size = 24}) {
    return (
        <Avatar
            alt=""
            src={src || undefined}
            data-testid={src ? undefined : testId}
            imgProps={src ? {'data-testid': testId} : undefined}
            sx={{width: size, height: size, fontSize: Math.round(size * 0.45)}}
        >
            {src ? null : avatarInitials(label)}
        </Avatar>
    );
}

function SkippedStatus() {
    return (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{color: 'warning.main'}}>
            <RemoveCircleOutlineIcon data-testid="chart-mutation-skipped-icon" sx={{fontSize: 16}}/>
            <Typography variant="caption" sx={{fontWeight: 600, color: 'warning.main'}}>
                Skipped
            </Typography>
        </Stack>
    );
}

function extractAlreadyOnChartName(warning) {
    let match = warning.match(/^(.+?)\s+already on chart\.?$/i);
    if (match) return match[1].trim();
    match = warning.match(/^"(.+?)" is already on the chart\.$/i);
    if (match) return match[1].trim();
    match = warning.match(/^(.+?) is already on the chart\.$/i);
    return match ? match[1].trim() : null;
}

function PickEntityDecisionRow({
    mutation,
    chartReady,
    status,
    errorMessage,
    onPickCandidate,
    onCreateNew,
    onReject,
}) {
    const isBusy = status === 'applying';
    const query = mutation?.label || 'entity';
    const candidates = filterDistinctPickCandidates(
        Array.isArray(mutation?.candidates) ? mutation.candidates : []
    );

    return (
        <Box
            data-testid={`chart-mutation-row-${mutationKey(mutation)}`}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                px: 1,
                py: 0.75,
                borderRadius: 1,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Typography variant="body2" sx={{fontSize: 12, fontWeight: 500}}>
                {`Which "${query}"?`}
            </Typography>

            {status === 'applied' ? (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{color: 'success.main'}}>
                    <CheckCircleOutlineIcon sx={{fontSize: 16}}/>
                    <Typography variant="caption" sx={{fontWeight: 600}}>Added</Typography>
                </Stack>
            ) : status === 'rejected' ? (
                <SkippedStatus/>
            ) : status === 'error' && !isHiddenChartMutationMessage(errorMessage) ? (
                <Typography variant="caption" color="error">
                    {formatChartErrorForUser(errorMessage) || 'This change could not be applied.'}
                </Typography>
            ) : (
                <>
                    <Stack direction="column" spacing={0.5}>
                        {candidates.map((candidate, candidateIndex) => {
                            const candidateRef = candidate?.entityRef || candidate?.id || '';
                            const candidateLabel = displayName(candidate?.label, candidateRef, query || 'entity');
                            const subtitle = uniqueCandidateSubtitle(candidate, candidates);
                            const ariaLabel = subtitle
                                ? `Add ${candidateLabel} — ${subtitle}`
                                : `Add ${candidateLabel}`;
                            return (
                                <Button
                                    key={candidateRef || `${candidateLabel}-${candidateIndex}`}
                                    size="small"
                                    variant="outlined"
                                    disabled={!chartReady || isBusy}
                                    onClick={() => onPickCandidate?.(candidate)}
                                    aria-label={ariaLabel}
                                    sx={{justifyContent: 'flex-start', textTransform: 'none', py: 0.5}}
                                >
                                    <Stack alignItems="flex-start" spacing={0}>
                                        <Typography variant="caption" sx={{fontWeight: 600, fontSize: 12}}>
                                            {candidateLabel}
                                        </Typography>
                                        {subtitle ? (
                                            <Typography variant="caption" color="text.secondary" sx={{fontSize: 10}}>
                                                {subtitle}
                                            </Typography>
                                        ) : null}
                                    </Stack>
                                </Button>
                            );
                        })}
                    </Stack>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            className="chart-mutation-compact-action"
                            onClick={onReject}
                            disabled={isBusy}
                            aria-label={`Skip choosing ${query}`}
                            sx={COMPACT_MUTATION_ACTION_SX}
                        >
                            Skip
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            className="chart-mutation-compact-action"
                            disabled={!chartReady || isBusy}
                            onClick={onCreateNew}
                            aria-label={`Add ${query} as new entity`}
                            startIcon={isBusy ? <CircularProgress size={10} color="inherit"/> : null}
                            sx={COMPACT_MUTATION_ACTION_SX}
                        >
                            Add
                        </Button>
                    </Stack>
                </>
            )}
        </Box>
    );
}

function MutationDecisionRow({
    mutation,
    labelIndex,
    chartReady,
    status,
    errorMessage,
    onApprove,
    onReject,
    autoApplyMode = false,
    imageUrl = '',
    fromImageUrl = '',
    toImageUrl = '',
}) {
    const isBusy = status === 'applying';
    const description = describeMutation(mutation, labelIndex);
    const actionVerb = mutationActionVerb(mutation);
    const isConnect = mutation?.op === 'add_arrow' || mutation?.op === 'update_arrow';

    return (
        <Box
            data-testid={`chart-mutation-row-${mutationKey(mutation)}`}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                px: 1,
                py: 0.75,
                borderRadius: 1,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Stack direction="row" spacing={0.75} alignItems="center">
                {isConnect ? (
                    <Stack direction="row" spacing={0.25} alignItems="center" sx={{flexShrink: 0}}>
                        <MutationEntityAvatar
                            src={fromImageUrl}
                            label={mutation.fromLabel || displayRef(mutation.fromEntityRef, labelIndex)}
                            testId={`chart-mutation-entity-image-${mutation.fromEntityRef}`}
                            size={24}
                        />
                        <Typography variant="caption" sx={{fontSize: 11, color: 'text.secondary', px: 0.15}}>
                            →
                        </Typography>
                        <MutationEntityAvatar
                            src={toImageUrl}
                            label={mutation.toLabel || displayRef(mutation.toEntityRef, labelIndex)}
                            testId={`chart-mutation-entity-image-${mutation.toEntityRef}`}
                            size={24}
                        />
                    </Stack>
                ) : (
                    <MutationEntityAvatar
                        src={imageUrl}
                        label={mutation.label || displayRef(mutation.entityRef, labelIndex)}
                        testId={`chart-mutation-entity-image-${mutation.entityRef || mutationKey(mutation)}`}
                        size={28}
                    />
                )}
                <Typography variant="body2" sx={{fontSize: 12, fontWeight: 500, wordBreak: 'break-word'}}>
                    {description}
                </Typography>
            </Stack>

            {status === 'applied' ? (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{color: 'success.main'}}>
                    <CheckCircleOutlineIcon sx={{fontSize: 16}}/>
                    <Typography variant="caption" sx={{fontWeight: 600}}>Added</Typography>
                </Stack>
            ) : status === 'rejected' ? (
                <SkippedStatus/>
            ) : status === 'error' && !isHiddenChartMutationMessage(errorMessage) ? (
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="caption" color="error" sx={{wordBreak: 'break-word'}}>
                        {formatChartErrorForUser(errorMessage) || 'This change could not be applied.'}
                    </Typography>
                    {!autoApplyMode ? (
                        <Button size="small" onClick={onApprove} disabled={!chartReady || isBusy}>
                            Retry
                        </Button>
                    ) : null}
                </Stack>
            ) : autoApplyMode ? (
                isBusy ? (
                    <CircularProgress size={14}/>
                ) : null
            ) : (
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{width: 'auto', alignSelf: 'flex-end'}}>
                    <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        className="chart-mutation-compact-action"
                        onClick={onReject}
                        disabled={isBusy}
                        aria-label={`Skip ${actionVerb} ${description}`}
                        sx={COMPACT_MUTATION_ACTION_SX}
                    >
                        Skip
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        className="chart-mutation-compact-action"
                        onClick={onApprove}
                        disabled={!chartReady || isBusy}
                        aria-label={`${actionVerb} ${description}`}
                        startIcon={isBusy ? <CircularProgress size={10} color="inherit"/> : null}
                        sx={COMPACT_MUTATION_ACTION_SX}
                    >
                        {actionVerb}
                    </Button>
                </Stack>
            )}
        </Box>
    );
}

/**
 * Preview + per-mutation confirm UI for a ChartMutationPlan from SSE `chart-mutations-proposed`.
 *
 * Each change is approved or skipped individually. Refine the list by prompting in chat.
 *
 * @param {{
 *   plan: {schemaVersion?: number, chartId?: string, summary?: string, mutations?: Array<object>, warnings?: Array<string>},
 *   onReject: () => void,
 *   onApplied?: (result: object) => void,
 *   onWorkLog?: (entries: string[]) => void,
 *   onEntityHandled?: (entityRef: string, action: 'applied'|'rejected') => void,
 * }} props
 */
const ChartMutationPreview = ({plan, onReject, onApplied, onWorkLog, onEntityHandled}) => {
    const canApplyForChart = useChartAiBridgeStore((s) => s.canApply);
    const applyMutations = useChartAiBridgeStore((s) => s.applyMutations);
    const chartEntities = useChartAiBridgeStore((s) => s.chartEntities);

    const [mutationStatuses, setMutationStatuses] = useState({});
    const [mutationErrors, setMutationErrors] = useState({});
    const autoApplyAttemptedRef = useRef(false);

    const mutations = useMemo(
        () => filterDisplayableMutations(Array.isArray(plan?.mutations) ? plan.mutations : []),
        [plan?.mutations]
    );
    const labelIndex = useMemo(() => buildMutationLabelIndex(mutations), [mutations]);
    const chartReady = canApplyForChart(plan?.chartId);
    const autoApplyMode = Boolean(plan?.autoApply);

    const pendingEntries = useMemo(() => mutations
        .map((mutation, index) => ({
            mutation,
            index,
            key: mutationKey(mutation, index),
        }))
        .filter(({key}) => {
            const status = mutationStatuses[key];
            return status !== 'applied' && status !== 'rejected';
        }), [mutationStatuses, mutations]);

    const pendingCount = pendingEntries.length;

    const isApplyingAny = useMemo(
        () => Object.values(mutationStatuses).some((status) => status === 'applying'),
        [mutationStatuses]
    );

    const emitWorkLog = useCallback((appliedMutations) => {
        const entries = buildChartApplyWorkLogEntries(appliedMutations);
        if (entries.length > 0) {
            onWorkLog?.(entries);
        }
    }, [onWorkLog]);

    const applyMutationBatch = useCallback(async (entries, {autoApply = false} = {}) => {
        if (!chartReady || typeof applyMutations !== 'function') {
            throw new Error('Open chart to apply changes');
        }
        if (entries.length === 0) {
            return null;
        }
        setMutationStatuses((current) => {
            const next = {...current};
            entries.forEach(({key}) => {
                next[key] = 'applying';
            });
            return next;
        });
        setMutationErrors((current) => {
            const next = {...current};
            entries.forEach(({key}) => {
                delete next[key];
            });
            return next;
        });
        const batchMutations = entries.map(({mutation}) => mutation);
        const needsMaterialize = batchMutations.some((mutation) => mutation?.op === 'create_local_entity');
        try {
            const materialized = needsMaterialize
                ? await materializeChartMutations(batchMutations)
                : batchMutations;
            const result = await applyMutations({
                ...plan,
                autoApply,
                mutations: materialized,
            });
            onApplied?.(result);
            if (result?.failed?.length) {
                const nextStatuses = {};
                const nextErrors = {};
                for (const failure of result.failed) {
                    const failedIndex = entries.findIndex(({mutation}) => mutation === failure?.mutation);
                    const entry = failedIndex >= 0
                        ? entries[failedIndex]
                        : entries[0];
                    nextStatuses[entry.key] = 'error';
                    nextErrors[entry.key] = formatChartErrorForUser(failure?.error);
                }
                for (const entry of entries) {
                    if (nextStatuses[entry.key]) continue;
                    nextStatuses[entry.key] = 'applied';
                    if (entry.mutation?.op === 'add_entity_card' && entry.mutation.entityRef) {
                        onEntityHandled?.(entry.mutation.entityRef, 'applied');
                    }
                    if (entry.mutation?.op === 'create_local_entity' && entry.mutation.label) {
                        onEntityHandled?.(entry.mutation.label, 'applied');
                    }
                }
                setMutationStatuses((current) => ({...current, ...nextStatuses}));
                setMutationErrors((current) => ({...current, ...nextErrors}));
                throw new Error(formatChartErrorForUser(result.failed[0]?.error));
            }
            const appliedStatuses = {};
            for (const entry of entries) {
                appliedStatuses[entry.key] = 'applied';
                if (entry.mutation?.op === 'add_entity_card' && entry.mutation.entityRef) {
                    onEntityHandled?.(entry.mutation.entityRef, 'applied');
                }
                if (entry.mutation?.op === 'create_local_entity' && entry.mutation.label) {
                    onEntityHandled?.(entry.mutation.label, 'applied');
                }
            }
            setMutationStatuses((current) => ({...current, ...appliedStatuses}));
            emitWorkLog(batchMutations);
            return result;
        } catch (error) {
            const message = error?.message || 'Failed to apply chart change';
            if (isHiddenChartMutationMessage(message)) {
                setMutationStatuses((current) => {
                    const next = {...current};
                    entries.forEach(({key}) => {
                        if (next[key] === 'applying') {
                            delete next[key];
                        }
                    });
                    return next;
                });
                throw error;
            }
            setMutationStatuses((current) => {
                const next = {...current};
                entries.forEach(({key}) => {
                    if (next[key] === 'applying') {
                        next[key] = 'error';
                    }
                });
                return next;
            });
            setMutationErrors((current) => {
                const next = {...current};
                entries.forEach(({key}) => {
                    if (next[key] === 'applying' || !next[key]) {
                        next[key] = message;
                    }
                });
                return next;
            });
            throw error;
        }
    }, [applyMutations, chartReady, emitWorkLog, onApplied, onEntityHandled, plan]);

    const applyResolvedEntity = useCallback(async (pendingMutation, entityRef, key) => {
        if (!chartReady || typeof applyMutations !== 'function') {
            throw new Error('Open chart to apply changes');
        }
        setMutationStatuses((current) => ({...current, [key]: 'applying'}));
        setMutationErrors((current) => {
            const next = {...current};
            delete next[key];
            return next;
        });
        const resolvedBatch = buildResolvedAddBatch(pendingMutation, entityRef, plan?.mutations);
        const result = await applyMutations({
            ...plan,
            autoApply: false,
            mutations: resolvedBatch,
        });
        onApplied?.(result);
        if (result?.failed?.length) {
            throw new Error(formatChartErrorForUser(result.failed[0]?.error));
        }
        setMutationStatuses((current) => ({...current, [key]: 'applied'}));
        onEntityHandled?.(entityRef, 'applied');
        emitWorkLog(resolvedBatch);
    }, [applyMutations, chartReady, emitWorkLog, onApplied, onEntityHandled, plan]);

    const applyAllMutations = useCallback(
        () => applyMutationBatch(
            mutations
                .map((mutation, index) => ({
                    mutation,
                    index,
                    key: mutationKey(mutation, index),
                }))
                .filter(({mutation}) => mutation?.op !== 'pick_entity'),
            {autoApply: true}
        ),
        [applyMutationBatch, mutations]
    );

    useEffect(() => {
        autoApplyAttemptedRef.current = false;
    }, [plan?.chartId, plan?.summary, plan?.autoApply]);

    useEffect(() => {
        if (!autoApplyMode || !chartReady || mutations.length === 0) {
            return undefined;
        }
        if (autoApplyAttemptedRef.current) {
            return undefined;
        }
        autoApplyAttemptedRef.current = true;
        let cancelled = false;
        applyAllMutations().catch((error) => {
            if (cancelled) return;
            console.error('[ChartMutationPreview] Auto-apply failed', error);
        });
        return () => {
            cancelled = true;
        };
    }, [applyAllMutations, autoApplyMode, chartReady, mutations.length]);

    const applySingleMutation = useCallback(async (mutation, key) => {
        if (!chartReady || typeof applyMutations !== 'function') {
            throw new Error('Open chart to apply changes');
        }
        setMutationStatuses((current) => ({...current, [key]: 'applying'}));
        setMutationErrors((current) => {
            const next = {...current};
            delete next[key];
            return next;
        });
        const relatedArrows = relatedArrowMutations(plan?.mutations, mutation);
        const batch = [mutation, ...relatedArrows];
        const materialized = mutation?.op === 'create_local_entity'
            ? await materializeChartMutations(batch)
            : batch;
        const subPlan = {
            ...plan,
            autoApply: false,
            mutations: materialized,
        };
        const result = await applyMutations(subPlan);
        onApplied?.(result);
        if (result?.failed?.length) {
            throw new Error(formatChartErrorForUser(result.failed[0]?.error));
        }
        setMutationStatuses((current) => ({...current, [key]: 'applied'}));
        if (mutation?.op === 'add_entity_card' && mutation.entityRef) {
            onEntityHandled?.(mutation.entityRef, 'applied');
        }
        if (mutation?.op === 'create_local_entity' && mutation.label) {
            onEntityHandled?.(mutation.label, 'applied');
        }
        emitWorkLog(batch);
    }, [applyMutations, chartReady, emitWorkLog, onApplied, onEntityHandled, plan]);

    const handleApprove = useCallback(async (mutation, index) => {
        const key = mutationKey(mutation, index);
        try {
            await applySingleMutation(mutation, key);
        } catch (error) {
            if (isHiddenChartMutationMessage(error?.message)) {
                setMutationStatuses((current) => {
                    const next = {...current};
                    delete next[key];
                    return next;
                });
                return;
            }
            setMutationStatuses((current) => ({...current, [key]: 'error'}));
            setMutationErrors((current) => ({
                ...current,
                [key]: error?.message || 'Failed to apply chart change',
            }));
        }
    }, [applySingleMutation]);

    const handlePickCandidate = useCallback(async (mutation, index, candidate) => {
        const key = mutationKey(mutation, index);
        try {
            await applyResolvedEntity(mutation, candidate?.entityRef, key);
        } catch (error) {
            if (isHiddenChartMutationMessage(error?.message)) {
                setMutationStatuses((current) => {
                    const next = {...current};
                    delete next[key];
                    return next;
                });
                return;
            }
            setMutationStatuses((current) => ({...current, [key]: 'error'}));
            setMutationErrors((current) => ({
                ...current,
                [key]: error?.message || 'Failed to apply chart change',
            }));
        }
    }, [applyResolvedEntity]);

    const handlePickCreateNew = useCallback(async (mutation, index) => {
        const key = mutationKey(mutation, index);
        try {
            await applySingleMutation({
                op: 'create_local_entity',
                label: mutation.label,
                entityType: mutation.entityType || 'ORG',
                suggestedAnchor: mutation.suggestedAnchor,
            }, key);
        } catch (error) {
            if (isHiddenChartMutationMessage(error?.message)) {
                setMutationStatuses((current) => {
                    const next = {...current};
                    delete next[key];
                    return next;
                });
                return;
            }
            setMutationStatuses((current) => ({...current, [key]: 'error'}));
            setMutationErrors((current) => ({
                ...current,
                [key]: error?.message || 'Failed to apply chart change',
            }));
        }
    }, [applySingleMutation]);

    const handleReject = useCallback((mutation, index) => {
        const key = mutationKey(mutation, index);
        setMutationStatuses((current) => ({...current, [key]: 'rejected'}));
        if (mutation?.op === 'add_entity_card' && mutation.entityRef) {
            onEntityHandled?.(mutation.entityRef, 'rejected');
        }
    }, [onEntityHandled]);

    const bulkApplicablePending = useMemo(
        () => pendingEntries.filter(({mutation}) => isBulkApplyReadyMutation(mutation)),
        [pendingEntries]
    );
    const showBulkActions = !autoApplyMode && bulkApplicablePending.length >= 2;

    const handleAddAll = useCallback(async () => {
        try {
            await applyMutationBatch(bulkApplicablePending, {autoApply: false});
        } catch (error) {
            console.error('[ChartMutationPreview] Add all failed', error);
        }
    }, [applyMutationBatch, bulkApplicablePending]);

    const handleDismissAll = useCallback(() => {
        const rejectedStatuses = {};
        for (const {mutation, key} of pendingEntries) {
            rejectedStatuses[key] = 'rejected';
            if (mutation?.op === 'add_entity_card' && mutation.entityRef) {
                onEntityHandled?.(mutation.entityRef, 'rejected');
            }
        }
        setMutationStatuses((current) => ({...current, ...rejectedStatuses}));
    }, [onEntityHandled, pendingEntries]);

    if (!plan) {
        return null;
    }

    return (
        <Box
            data-testid="chart-mutation-preview"
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                bgcolor: 'background.default',
            }}
        >
            <Stack spacing={1}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <AccountTreeOutlinedIcon sx={{fontSize: 16, color: 'text.secondary'}}/>
                    <Typography variant="subtitle2" sx={{fontWeight: 600, fontSize: 13}}>
                        Suggested chart changes
                    </Typography>
                </Stack>

                {isAnalystVisibleChartPlanSummary(plan.summary) ? (
                    <Typography variant="body2" color="text.secondary" sx={{fontSize: 12}}>
                        {plan.summary}
                    </Typography>
                ) : null}

                {autoApplyMode ? (
                    <Typography variant="caption" color="text.secondary" sx={{fontSize: 11}}>
                        Adding entities to your empty chart automatically.
                    </Typography>
                ) : null}

                {mutations.length > 0 ? (
                    <Stack spacing={0.75}>
                        {mutations.map((mutation, index) => {
                            const key = mutationKey(mutation, index);
                            if (mutation?.op === 'pick_entity') {
                                return (
                                    <PickEntityDecisionRow
                                        key={key}
                                        mutation={mutation}
                                        chartReady={chartReady}
                                        status={mutationStatuses[key] || null}
                                        errorMessage={mutationErrors[key]}
                                        onPickCandidate={(candidate) => handlePickCandidate(mutation, index, candidate)}
                                        onCreateNew={() => handlePickCreateNew(mutation, index)}
                                        onReject={() => handleReject(mutation, index)}
                                    />
                                );
                            }
                            const images = resolveMutationEndpointImages(mutation, chartEntities);
                            return (
                                <MutationDecisionRow
                                    key={key}
                                    mutation={mutation}
                                    labelIndex={labelIndex}
                                    chartReady={chartReady}
                                    status={mutationStatuses[key] || null}
                                    errorMessage={mutationErrors[key]}
                                    onApprove={() => handleApprove(mutation, index)}
                                    onReject={() => handleReject(mutation, index)}
                                    autoApplyMode={autoApplyMode}
                                    imageUrl={images.fromUrl}
                                    fromImageUrl={images.fromUrl}
                                    toImageUrl={images.toUrl}
                                />
                            );
                        })}
                    </Stack>
                ) : (
                    <Typography variant="caption" color="text.secondary">
                        No changes in this plan.
                    </Typography>
                )}

                {showBulkActions ? (
                    <Stack
                        direction="row"
                        justifyContent="center"
                        spacing={2}
                        sx={{pt: 2.5, mt: 0.5}}
                    >
                        <Button
                            size="small"
                            color="inherit"
                            variant="outlined"
                            onClick={handleDismissAll}
                            disabled={isApplyingAny}
                            aria-label="Dismiss all chart changes"
                            sx={{minWidth: 0, px: 1.25}}
                        >
                            Dismiss All
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={handleAddAll}
                            disabled={!chartReady || isApplyingAny || bulkApplicablePending.length === 0}
                            aria-label="Add all chart changes"
                            startIcon={isApplyingAny ? <CircularProgress size={12} color="inherit"/> : null}
                            sx={{minWidth: 0, px: 1.25}}
                        >
                            Add all
                        </Button>
                    </Stack>
                ) : null}

                {pendingCount === 0 && mutations.length > 0 ? (
                    <Stack direction="row" justifyContent="flex-end">
                        <Button size="small" color="inherit" onClick={onReject} aria-label="Dismiss chart suggestions">
                            Dismiss
                        </Button>
                    </Stack>
                ) : null}
            </Stack>
        </Box>
    );
};

export default ChartMutationPreview;
